/**
 * Canonical locale handling for Atlas.
 *
 * Every locale is a **2-letter BCP-47 language tag**. `en` is canonical and is
 * also the fallback for every other locale.
 *
 * Locale formats have historically drifted across the product (`en-US` in the
 * text package, `en_US` in the help center article table, `en` in company
 * settings). Everything funnels through {@link normalizeLocale} so a stored
 * value can never silently miss a catalog lookup.
 */

/** Locales Atlas ships. Order is meaningful: index 0 is the canonical one. */
export const LOCALES = ["en", "es", "pt", "fr", "de"] as const;

export type TLocale = typeof LOCALES[number];

/** Canonical locale, and the fallback for any other value. */
export const DEFAULT_LOCALE: TLocale = "en";

const LOCALE_SET: ReadonlySet<string> = new Set<string>(LOCALES);

export function isLocale(value: unknown): value is TLocale {
  return typeof value === "string" && LOCALE_SET.has(value);
}

/**
 * Normalize any observed locale spelling into a canonical 2-letter tag.
 *
 * `en-US`, `en_US`, `EN` and `en-Latn-US` all resolve to `en`. Unknown,
 * malformed or missing values resolve to {@link DEFAULT_LOCALE}; this function
 * never throws, so a bad persisted locale degrades to English instead of
 * breaking rendering.
 */
export function normalizeLocale(value: unknown): TLocale {
  if (typeof value !== "string") return DEFAULT_LOCALE;

  const language = value.trim().toLowerCase().split(/[-_]/)[0];

  return isLocale(language) ? language : DEFAULT_LOCALE;
}

/**
 * `og:locale` (Open Graph) requires a `language_TERRITORY` value, which is the
 * one place where the 2-letter canonical form is not valid on output. Atlas
 * Portuguese content is Brazilian.
 */
export const OG_LOCALES: Record<TLocale, string> = {
  en: "en_US",
  es: "es_ES",
  pt: "pt_BR",
  fr: "fr_FR",
  de: "de_DE",
};

/** Convert any locale spelling into an Open Graph `language_TERRITORY` value. */
export function toOgLocale(value: unknown): string {
  return OG_LOCALES[normalizeLocale(value)];
}
